<template>
  <div class="profile-container">
    <!-- 顶部渐变背景 -->
    <div class="header-wrapper">
      <div class="header-bg"></div>
      <div class="floating-circle circle-1"></div>
      <div class="floating-circle circle-2"></div>
      <div class="floating-circle circle-3"></div>
    </div>

    <div class="content-wrapper">
      <!-- 用户信息卡片 -->
      <div class="user-card">
        <div class="user-header">
          <div class="avatar-section">
            <div class="avatar-ring"></div>
            <img :src="user.avatar || defaultAvatar" class="avatar" />
            <div class="vip-badge" v-if="hasAnyBankVip">VIP</div>
          </div>
          <div class="user-info">
            <h1 class="nickname">{{ user.nickname || '未登录' }}</h1>
            <div class="bank-vip-list">
              <div
                v-for="item in bankVipList"
                :key="item.bank_id"
                class="bank-vip-row"
              >
                <span class="bank-vip-name">{{ item.bank_name }}</span>
                <span class="bank-vip-tier" :class="{ inactive: !item.has_access }">
                  {{ item.has_access ? (item.package_name || '已开通') : '未开通' }}
                </span>
                <span class="bank-vip-days" :class="{ inactive: !item.has_access }">
                  {{ formatBankVipDaysText(item) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 数据概览 -->
        <div class="stats-section" @click="toggleStats">
          <div class="stats-header">
            <span class="stats-title">数据概览</span>
            <van-icon
              name="arrow-down"
              size="18"
              :class="{ rotated: showStats }"
            />
          </div>
          <div class="stats-content" :class="{ collapsed: !showStats }">
            <div class="stat-item" v-for="stat in statList" :key="stat.label">
              <div class="stat-icon" :style="{ background: stat.bgColor }">
                <van-icon :name="stat.icon" :color="stat.color" size="18" />
              </div>
              <div class="stat-info">
                <span class="stat-value">{{ stat.value }}</span>
                <span class="stat-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 我的服务 -->
      <div class="menu-section">
        <div class="section-title">
          <span class="title-icon">🎵</span>
          <h2>我的服务</h2>
        </div>
        <div class="menu-grid">
          <div
            class="menu-item"
            v-for="item in menuItems"
            :key="item.label"
            @click="handleMenuClick(item)"
          >
            <div class="menu-icon" :style="{ background: item.bgColor }">
              <van-icon :name="item.icon" :color="item.color" size="22" />
            </div>
            <span class="menu-text">{{ item.label }}</span>
          </div>
        </div>
      </div>

      <!-- 快捷入口 -->
      <div class="quick-action">
        <div class="quick-card" @click="goToQuiz">
          <div class="quick-icon">
            <span>🎯</span>
          </div>
          <div class="quick-info">
            <h3>开始答题</h3>
            <p>每日挑战，赢取积分奖励</p>
          </div>
          <van-icon name="arrow" color="white" size="20" />
        </div>
      </div>

      <!-- VIP特权 -->
      <div class="vip-section">
        <div class="vip-card">
          <div class="vip-header">
            <span class="vip-icon">👑</span>
            <span class="vip-title">VIP专属特权</span>
          </div>
          <div class="vip-benefits">
            <div class="benefit-item">
              <van-icon name="check-circle-o" color="#4CD964" size="18" />
              <span>无限答题次数</span>
            </div>
            <div class="benefit-item">
              <van-icon name="check-circle-o" color="#4CD964" size="18" />
              <span>专属题目库</span>
            </div>
            <div class="benefit-item">
              <van-icon name="check-circle-o" color="#4CD964" size="18" />
              <span>双倍积分奖励</span>
            </div>
          </div>
          <div class="vip-action" @click="goToVip">立即开通</div>
        </div>
      </div>

      <!-- 退出登录 -->
      <div class="logout-section">
        <van-button
          type="default"
          block
          round
          @click="handleLogout"
          class="logout-btn"
        >
          退出登录
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Dialog, Toast } from 'vant'
import { inviteShareFriends } from '@/utils/shareInvite'
import { formatBankVipDaysText } from '@/utils/vipDisplay'

export default {
  name: 'Profile',
  data() {
    return {
      defaultAvatar: 'https://img.yzcdn.cn/vant/cat.jpeg',
      showStats: true
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    bankVipList() {
      return this.user.bank_vips || []
    },
    hasAnyBankVip() {
      return this.bankVipList.some((item) => item && item.has_access)
    },
    statList() {
      return [
        {
          value: this.user.total_score || 0,
          label: '总积分',
          icon: 'award-o',
          color: '#5856D6',
          bgColor: 'rgba(88, 86, 214, 0.1)'
        },
        {
          value: this.user.answer_count || 0,
          label: '答题次数',
          icon: 'edit',
          color: '#FF9500',
          bgColor: 'rgba(255, 149, 0, 0.1)'
        }
      ]
    },
    menuItems() {
      const baseItems = [
        { icon: 'notes-o', label: '答题记录', action: 'goToResult', color: '#fff', bgColor: 'linear-gradient(135deg, #5856D6, #AF52DE)' },
        { icon: 'bill-o', label: '我的订单', action: 'goToOrders', color: '#fff', bgColor: 'linear-gradient(135deg, #FF9500, #FF6D00)' },
        { icon: 'gem-o', label: 'VIP中心', action: 'goToVip', color: '#fff', bgColor: 'linear-gradient(135deg, #FF2D55, #FF3B30)' },
        { icon: 'share-o', label: '邀请好友', action: 'inviteFriends', color: '#fff', bgColor: 'linear-gradient(135deg, #5AC8FA, #007AFF)' },
        { icon: 'info-o', label: '关于我们', action: 'showAbout', color: '#fff', bgColor: 'linear-gradient(135deg, #007AFF, #5856D6)' }
      ]
      if (!this.hasAnyBankVip) {
        baseItems.splice(3, 0, {
          icon: 'arrow-up',
          label: '升级VIP',
          action: 'goToUpgrade',
          color: '#fff',
          bgColor: 'linear-gradient(135deg, #FF3B30, #FF9500)'
        })
      }
      return baseItems
    }
  },
  async mounted() {
    await this.refreshUser()
  },
  activated() {
    this.refreshUser()
  },
  methods: {
    formatBankVipDaysText,
    async refreshUser() {
      try {
        await this.$store.dispatch('fetchUserInfo')
      } catch (err) {
        console.error(err)
      }
    },
    goToResult() {
      this.$router.push('/records')
    },
    goToVip() {
      this.$router.push('/vip')
    },
    goToUpgrade() {
      this.$router.push('/vip/upgrade')
    },
    goToOrders() {
      this.$router.push('/orders')
    },
    goToQuiz() {
      this.$router.push('/quiz')
    },
    goToSettings() {
      Toast('设置功能开发中')
    },
    inviteFriends() {
      inviteShareFriends()
    },
    async showAbout() {
      try {
        const response = await this.$http.get('/api/about')
        const about = response.data.data.about
        if (about) {
          const contactHtml = about.contact
            ? '<p>联系我们：' + about.contact + '</p>'
            : ''
          Dialog.alert({
            title: about.title || '关于我们',
            message:
              '<div style="text-align:left;padding:10px 0">' +
              '<p style="margin-bottom:15px;line-height:1.8">' +
              (about.content || '') +
              '</p>' +
              contactHtml +
              '</div>',
            confirmButtonText: '确定'
          })
        } else {
          Toast('暂无关于信息')
        }
      } catch (err) {
        Toast.fail('加载失败')
      }
    },
    async contactService() {
      try {
        const response = await this.$http.get('/api/about')
        const about = response.data.data.about
        if (about && about.contact) {
          window.location.href = 'tel:' + about.contact
        } else {
          Toast('暂无联系方式')
        }
      } catch (err) {
        Toast.fail('加载失败')
      }
    },
    handleMenuClick(item) {
      if (typeof this[item.action] === 'function') {
        this[item.action]()
      }
    },
    handleLogout() {
      Dialog.confirm({ title: '提示', message: '确定要退出登录吗？' })
        .then(() => {
          this.$store.commit('LOGOUT')
          Toast.success('已退出登录')
          this.$router.push('/login')
        })
        .catch(() => {})
    },
    toggleStats() {
      this.showStats = !this.showStats
    }
  }
}
</script>

<style scoped>
.profile-container {
  min-height: 100vh;
  background: #f7f8fa;
  padding-bottom: calc(88px + env(safe-area-inset-bottom));
  position: relative;
  overflow: hidden;
}

.header-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 260px;
  overflow: hidden;
  z-index: 1;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #5856d6 0%, #af52de 60%, #ff2d55 100%);
}

.floating-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 130px;
  height: 130px;
  top: 30px;
  right: -40px;
}

.circle-2 {
  width: 90px;
  height: 90px;
  top: 120px;
  left: -30px;
  animation-delay: 2s;
}

.circle-3 {
  width: 70px;
  height: 70px;
  bottom: 50px;
  right: 50px;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-15px) scale(1.05);
    opacity: 0.8;
  }
}

.content-wrapper {
  position: relative;
  z-index: 2;
  padding-top: 20px;
}

.user-card {
  margin: 0 16px;
  margin-top: calc(20px + env(safe-area-inset-top));
  background: #ffffff;
  border-radius: 28px;
  padding: 28px;
  box-shadow: 0 10px 30px rgba(88, 86, 214, 0.12);
}

.user-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f5;
}

.avatar-section {
  position: relative;
  flex-shrink: 0;
}

.avatar-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  border: 2px solid rgba(88, 86, 214, 0.25);
  animation: ring-pulse 2.5s ease-in-out infinite;
}

@keyframes ring-pulse {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.06);
  }
}

.avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid #fff;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.vip-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  padding: 2px 8px;
  background: linear-gradient(135deg, #ff9500, #ff3b30);
  border-radius: 10px;
  font-size: 10px;
  color: white;
  font-weight: 600;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(255, 149, 0, 0.3);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.nickname {
  font-size: 24px;
  font-weight: 700;
  color: #1c1c1e;
  margin: 0 0 8px 0;
}

.bank-vip-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.bank-vip-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
}

.bank-vip-name {
  flex: 0 0 auto;
  min-width: 64px;
  color: #1c1c1e;
  font-weight: 600;
}

.bank-vip-tier {
  flex: 1;
  color: #5856d6;
  font-weight: 500;
}

.bank-vip-tier.inactive,
.bank-vip-days.inactive {
  color: #8e8e93;
  font-weight: 400;
}

.bank-vip-days {
  flex: 0 0 auto;
  color: #666;
  text-align: right;
}

.stats-section {
  padding-top: 20px;
}

.stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  cursor: pointer;
}

.stats-title {
  font-size: 15px;
  font-weight: 600;
  color: #3a3a3c;
}

.stats-header .van-icon {
  transition: transform 0.3s ease;
  color: #8e8e93;
}

.stats-header .van-icon.rotated {
  transform: rotate(180deg);
}

.stats-content {
  overflow: hidden;
  transition: all 0.35s ease;
  max-height: 300px;
}

.stats-content.collapsed {
  max-height: 0;
  opacity: 0;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f7f8fa;
  border-radius: 16px;
  margin-bottom: 10px;
  transition: all 0.2s;
}

.stat-item:active {
  background: #f2f2f7;
  transform: scale(0.98);
}

.stat-item:last-child {
  margin-bottom: 0;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1c1c1e;
  display: block;
}

.stat-label {
  font-size: 12px;
  color: #8e8e93;
}

.menu-section {
  margin: 24px 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.title-icon {
  font-size: 20px;
}

.section-title h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1c1c1e;
  margin: 0;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 8px;
  background: white;
  border-radius: 22px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
}

.menu-item:active {
  transform: scale(0.96);
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
}

.menu-icon {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-text {
  font-size: 13px;
  color: #3a3a3c;
  font-weight: 500;
}

.quick-action {
  margin: 0 16px 24px;
}

.quick-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 26px;
  background: linear-gradient(135deg, #5856d6 0%, #af52de 100%);
  border-radius: 28px;
  box-shadow: 0 12px 30px rgba(88, 86, 214, 0.25);
  transition: all 0.2s;
}

.quick-card:active {
  transform: scale(0.98);
  box-shadow: 0 8px 20px rgba(88, 86, 214, 0.2);
}

.quick-icon {
  width: 54px;
  height: 54px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.quick-info {
  flex: 1;
}

.quick-info h3 {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0 0 6px 0;
}

.quick-info p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.vip-section {
  margin: 0 16px 24px;
}

.vip-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04);
}

.vip-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.vip-icon {
  font-size: 20px;
}

.vip-title {
  font-size: 17px;
  font-weight: 600;
  color: #1c1c1e;
}

.vip-benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(76, 217, 100, 0.1);
  border-radius: 14px;
}

.benefit-item span {
  font-size: 13px;
  color: #34c759;
  font-weight: 500;
}

.vip-action {
  text-align: center;
  padding: 14px;
  background: linear-gradient(135deg, #ff9500, #ff3b30);
  border-radius: 16px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(255, 149, 0, 0.25);
  transition: all 0.2s;
}

.vip-action:active {
  transform: scale(0.97);
  box-shadow: 0 4px 10px rgba(255, 149, 0, 0.2);
}

.logout-section {
  padding: 0 16px;
  margin-bottom: 44px;
}

.logout-btn {
  background: transparent;
  border: 1.5px solid #d1d1d6;
  color: #ff3b30;
  height: 50px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 16px;
}
</style>
